'use client';

import type { ReactNode } from 'react';
import { createContext, useState, useTransition, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';

type NavigateFunction = (href: string, options?: NavigateOptions) => void;

export const PageTransitionContext = createContext<boolean>(false);
export const PageTransitionNavigateContext = createContext<NavigateFunction>(() => {});
export const PageTransitionLinkPendingContext = createContext<(pending: boolean) => void>(() => {});

export const PageTransitionProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [linkPending, setLinkPending] = useState(false);

  // Combined loading state: true if either programmatic navigation or link navigation is pending
  const loading = isPending || linkPending;

  const navigate = useCallback<NavigateFunction>(
    (href, options) => {
      startTransition(() => {
        router.push(href, options);
      });
    },
    [router],
  );

  return (
    <PageTransitionContext.Provider value={loading}>
      <PageTransitionNavigateContext.Provider value={navigate}>
        <PageTransitionLinkPendingContext.Provider value={setLinkPending}>
          {children}
        </PageTransitionLinkPendingContext.Provider>
      </PageTransitionNavigateContext.Provider>
    </PageTransitionContext.Provider>
  );
};
